import React, { useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FolderOpen, Folder, Hash, Pin, Search, CheckSquare, GraduationCap, Settings, Plus, ChevronRight, Trash2 } from 'lucide-react';
import { useStore, selectAllTags } from '../../store';
import { useShallow } from 'zustand/shallow';
import { Avatar } from '../ui';
import { formatMb } from '../../utils';

const C = { amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623', amber400:'#C4811A', amber500:'#8B5A0A', ink:'#1A1814', inkLight:'#3D3A34', muted:'#7A7570', border:'#E8E4DC', surface:'#FAFAF8', white:'#FFFFFF' };
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

const NavItem: React.FC<{ icon:React.ReactNode; label:React.ReactNode; count?:number; active?:boolean; onClick:()=>void; onDoubleClick?:()=>void; indent?:boolean }> = ({ icon, label, count, active, onClick, onDoubleClick, indent }) => (
  <div onClick={onClick} onDoubleClick={onDoubleClick} style={{ display:'flex', alignItems:'center', gap:9, padding:`8px ${indent?24:12}px`, borderRadius:9, cursor:'pointer', background:active?C.amber100:'transparent', color:active?C.amber500:C.inkLight, transition:'background .12s', userSelect:'none' }}
    onMouseEnter={e=>{ if(!active)(e.currentTarget as HTMLDivElement).style.background=C.surface; }}
    onMouseLeave={e=>{ if(!active)(e.currentTarget as HTMLDivElement).style.background='transparent'; }}>
    <span style={{ flexShrink:0, opacity:active?1:0.65 }}>{icon}</span>
    <span style={{ flex:1, fontSize:13, fontWeight:active?700:500, fontFamily:font.body, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', minWidth:0 }}>{label}</span>
    {count !== undefined && <span style={{ fontSize:10, color:active?C.amber400:C.muted, fontWeight:600, fontFamily:font.body }}>{count}</span>}
  </div>
);

const Sidebar: React.FC = () => {
  const navigate  = useNavigate();
  const location  = useLocation();

  // Individual selectors — no inline objects
  const user          = useStore(s => s.user);
  const folders       = useStore(s => s.folders);
  const notes         = useStore(s => s.notes);
  const activeView    = useStore(s => s.activeView);
  const activeFolderId= useStore(s => s.activeFolderId);
  const activeTag     = useStore(s => s.activeTag);
  const setActiveView = useStore(s => s.setActiveView);
  const createFolder  = useStore(s => s.createFolder);
  const deleteFolder  = useStore(s => s.deleteFolder);
  const tags          = useStore(useShallow(selectAllTags));

  const [expanded, setExpanded]     = useState<Set<string>>(new Set());
  const [showNewFolder, setShowNew] = useState(false);
  const [newFolderName, setNewName] = useState('');
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const renameInputRef = useRef<HTMLInputElement | null>(null);
  const ignoreBlurRef = useRef(false);

  const rootFolders  = folders.filter(f => !f.parentId);
  const childrenOf   = (pid: string) => folders.filter(f => f.parentId === pid);

  const handleNewFolder = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;
    const colors = ['#F5A623','#C4811A','#16A34A','#7C3AED','#DB2777','#2563EB'];
    createFolder(newFolderName.trim(), colors[folders.length % colors.length], null);
    setNewName(''); setShowNew(false);
  };

  const startRename = (folderId: string, currentName: string) => {
    ignoreBlurRef.current = false;
    setEditingFolderId(folderId);
    setEditingName(currentName);
  };

  const cancelRename = () => {
    ignoreBlurRef.current = false;
    setEditingFolderId(null);
    setEditingName('');
  };

  const saveRename = async (folderId: string, originalName: string) => {
    const trimmedName = editingName.trim();
    if (trimmedName && trimmedName !== originalName) {
      await useStore.getState().updateFolder(folderId, { name: trimmedName });
    }
    cancelRename();
  };

  const toggle = (id: string) => setExpanded(s => { const n = new Set(s); n.has(id)?n.delete(id):n.add(id); return n; });

  return (
    <aside style={{ width:240, background:C.surface, borderRight:`1px solid ${C.border}`, display:'flex', flexDirection:'column', height:'100vh', flexShrink:0, overflow:'hidden' }}>
      {/* Logo */}
      <div style={{ padding:'18px 16px 12px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:10 }}>
        <div style={{ width:30, height:30, borderRadius:9, background:`linear-gradient(145deg,${C.amber200},${C.amber400})`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <svg width="14" height="14" viewBox="0 0 180 180" fill="none">
            <g transform="translate(90,88) rotate(-35)"><rect x="-12" y="-38" width="24" height="58" rx="7" fill="white"/><rect x="-12" y="-38" width="24" height="16" rx="7" fill="#F08030" opacity="0.9"/><polygon points="-12,20 12,20 0,44" fill="white" opacity="0.9"/></g>
            <circle cx="104" cy="102" r="42" fill="none" stroke="white" strokeWidth="13" opacity="0.95"/>
            <g transform="translate(126,124) rotate(45)"><rect x="-6" y="-4" width="30" height="10" rx="5" fill="white"/></g>
          </svg>
        </div>
        <span style={{ fontFamily:font.head, fontWeight:800, fontSize:18, letterSpacing:'-0.05em', color:C.ink }}>Jötr</span>
      </div>

      {/* Nav */}
      <div style={{ flex:1, overflowY:'auto', padding:'10px 8px' }}>
        <NavItem icon={<FolderOpen size={15}/>} label="All notes" count={notes.length} active={activeView==='all'} onClick={()=>setActiveView('all')}/>
        <NavItem icon={<Pin size={15}/>} label="Pinned" count={notes.filter(n=>n.isPinned).length} active={activeView==='pinned'} onClick={()=>setActiveView('pinned')}/>
        <NavItem icon={<Search size={15}/>} label="Search" active={activeView==='search'} onClick={()=>setActiveView('search')}/>
        <NavItem icon={<CheckSquare size={15}/>} label="Tasks" active={location.pathname==='/tasks'} onClick={()=>navigate('/tasks')}/>
        <NavItem icon={<GraduationCap size={15}/>} label="Academy" active={location.pathname==='/academy'} onClick={()=>navigate('/academy')}/>

        {/* Folders header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 12px 4px' }}>
          <span style={{ fontSize:10, fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:font.body }}>Folders</span>
          <button onClick={()=>setShowNew(s=>!s)} style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, padding:2, display:'flex', borderRadius:5 }}><Plus size={13}/></button>
        </div>

        {showNewFolder && (
          <form onSubmit={handleNewFolder} style={{ padding:'4px 8px 8px' }}>
            <input autoFocus value={newFolderName} onChange={e=>setNewName(e.target.value)}
              placeholder="Folder name" onBlur={()=>{ if(!newFolderName)setShowNew(false); }}
              style={{ width:'100%', height:32, padding:'0 10px', borderRadius:7, border:`1.5px solid ${C.amber300}`, fontFamily:font.body, fontSize:13, outline:'none', background:C.white }}/>
          </form>
        )}

        {rootFolders.map(f => {
          const kids = childrenOf(f.id);
          const isExp = expanded.has(f.id);
          const isActive = activeView==='folder' && activeFolderId===f.id;
          const labelColor = isActive ? C.amber500 : C.inkLight;
          const folderLabel = editingFolderId === f.id ? (
            <input
              ref={renameInputRef}
              value={editingName}
              onChange={e => setEditingName(e.target.value)}
              autoFocus
              onBlur={() => {
                if (ignoreBlurRef.current) {
                  ignoreBlurRef.current = false;
                  return;
                }
                saveRename(f.id, f.name);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  saveRename(f.id, f.name);
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  ignoreBlurRef.current = true;
                  cancelRename();
                }
              }}
              style={{
                width:'100%',
                fontFamily:font.body,
                fontSize:13,
                fontWeight:isActive?700:500,
                color:labelColor,
                background:'transparent',
                border:'none',
                outline:'none',
                padding:0,
                margin:0,
                lineHeight:1.2,
                minWidth:0,
              }}
            />
          ) : f.name;
          return (
            <div key={f.id}>
              <div style={{ display:'flex', alignItems:'center' }}>
                <div style={{ flex:1 }}>
                  <NavItem icon={<Folder size={15} color={f.color}/>} label={folderLabel} count={notes.filter(n=>n.folderId===f.id).length}
                    active={isActive} onClick={()=>setActiveView('folder',f.id)} onDoubleClick={()=>startRename(f.id, f.name)}/>
                </div>
                {kids.length > 0 && (
                  <button onClick={()=>toggle(f.id)} style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, padding:'4px 6px', flexShrink:0, transform:isExp?'rotate(90deg)':'rotate(0)', transition:'transform .15s' }}>
                    <ChevronRight size={12}/>
                  </button>
                )}
                <button onClick={()=>deleteFolder(f.id)} style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, padding:'4px 6px', flexShrink:0 }} title="Delete folder">
                  <Trash2 size={11}/>
                </button>
              </div>
              {isExp && kids.map(c => {
                const childActive = activeView==='folder'&&activeFolderId===c.id;
                const childLabelColor = childActive ? C.amber500 : C.inkLight;
                const childLabel = editingFolderId === c.id ? (
                  <input
                    value={editingName}
                    onChange={e => setEditingName(e.target.value)}
                    autoFocus
                    onBlur={() => {
                      if (ignoreBlurRef.current) {
                        ignoreBlurRef.current = false;
                        return;
                      }
                      saveRename(c.id, c.name);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        saveRename(c.id, c.name);
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        ignoreBlurRef.current = true;
                        cancelRename();
                      }
                    }}
                    style={{
                      width:'100%',
                      fontFamily:font.body,
                      fontSize:13,
                      fontWeight:childActive?700:500,
                      color:childLabelColor,
                      background:'transparent',
                      border:'none',
                      outline:'none',
                      padding:0,
                      margin:0,
                      lineHeight:1.2,
                      minWidth:0,
                    }}
                  />
                ) : c.name;
                return (
                  <NavItem key={c.id} icon={<Folder size={13} color={c.color}/>} label={childLabel} count={notes.filter(n=>n.folderId===c.id).length}
                    active={childActive} onClick={()=>setActiveView('folder',c.id)} onDoubleClick={()=>startRename(c.id, c.name)} indent/>
                );
              })}
            </div>
          );
        })}

        {/* Tags */}
        {tags.length > 0 && (
          <>
            <div style={{ padding:'14px 12px 4px' }}>
              <span style={{ fontSize:10, fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:font.body }}>Tags</span>
            </div>
            {tags.map(tag => (
              <NavItem key={tag} icon={<Hash size={13}/>} label={tag}
                active={activeView==='tag'&&activeTag===tag} onClick={()=>setActiveView('tag',null,tag)}/>
            ))}
          </>
        )}
      </div>

      {/* User + storage */}
      <div style={{ padding:'10px 12px', borderTop:`1px solid ${C.border}` }}>
        {user && (
          <>
            <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:12 }}>
              <Avatar name={user.name} size={32}/>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ fontSize:13, fontWeight:700, color:C.ink, margin:0, fontFamily:font.body, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user.name}</p>
                <p style={{ fontSize:11, color:C.muted, margin:0, fontFamily:font.body }}>{user.plan==='pro'?'Pro plan':'Free plan'}</p>
              </div>
              <a href="/settings" style={{ color:C.muted, display:'flex' }}><Settings size={15}/></a>
            </div>
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                <span style={{ fontSize:10, color:C.muted, fontFamily:font.body }}>Storage</span>
                <span style={{ fontSize:10, color:C.muted, fontFamily:font.body }}>{formatMb(user.storageUsedMb)} / {formatMb(user.storageLimitMb)}</span>
              </div>
              <div style={{ height:3, background:C.border, borderRadius:2 }}>
                <div style={{ width:`${(user.storageUsedMb/user.storageLimitMb)*100}%`, height:'100%', background:`linear-gradient(90deg,${C.amber300},${C.amber400})`, borderRadius:2 }}/>
              </div>
            </div>
          </>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;